import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { fnGetDataSet, fnSave, fnGetTableById } from "@shared/api/restApi";
import { fnGetwhLocLotMultiItemScan } from "@shared/utils/InvUtils";
import {
  applySlip,
  getInitFormData,
  getBsn,
  fnPrintByReport,
  fnGetDate,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import { showResizablePopupModal } from "@widgets/CommonModal";
import {
  CellValueChangedEvent,
  ChangeColumnConfig,
  SnGridRef,
} from "@shared/components/GridV2/SnGridTypes";
import { SnFormRef } from "@widgets/SnForm";
import ComCreateUi from "@menu/COM/ComCreateUi";
import layout from "./SN_PRD_RUN_REG_001_CHEM.json";
import SN_PRD_RUN_REG_001_CHEM_POP_001 from "./SN_PRD_RUN_REG_001_CHEM_POP_001";
import SN_PRD_RUN_REG_001_CHEM_POP_002 from "./SN_PRD_RUN_REG_001_CHEM_POP_002";
import SN_PRD_RUN_REG_001_CHEM_POP_003 from "./SN_PRD_RUN_REG_001_CHEM_POP_003";
import SN_PRD_RUN_REG_001_CHEM_POP_004 from "./SN_PRD_RUN_REG_001_CHEM_POP_004";
import SN_PRD_RUN_REG_001_CHEM_POP_005 from "./SN_PRD_RUN_REG_001_CHEM_POP_005";
import { dateToString } from "@shared/utils/DateUtils";
import { UseApiService } from "@shared/hooks/useApiService";
import { RowNode } from "ag-grid-community";
import NumPadPopup from "@widgets/commonpopup/NumPadPopup";
import * as converter from "@shared/utils/ValueConverterUtils";

interface Props {
  Menuprops: menuInfo;
}

// 배합실적등록
const SN_PRD_RUN_REG_001_CHEM = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config: globalConfig } = global();
  const { apiPost } = UseApiService();
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });

  const [formDataSources, setFormDataSources] = useState<any>({
    freeform1: [],
  });

  const [headerInfo, setHeaderInfo] = useState<any>({
    grid1: { controls: [], data: {} },
    grid2: { controls: [], data: {} },
    grid3: { controls: [], data: {} },
  });

  const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    freeform1: React.createRef(),
  });

  const [formControls, setFormControls] = useState<any>({
    freeform1: [],
  });

  // 포커스이동 감지용 useState
  const [recipeNodeData, setRecipeNodeData] = useState<any>(null);

  const tpRecipe = useRef<string>("");
  const cdEquip = useRef<string>("");
  const equipVisible = useRef<boolean>(false); // grid2 헤더 설비(cd_equip) 컨트롤 표시 여부
  const cdBowl = useRef<string>("");
  const recentPopupEquip = useRef<string>("");
  const tmStart = useRef<Date | undefined>(undefined);
  const isWarned = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isNumPadOpen = useRef(false);

  const [masCodeBowls, setMasCodeBowls] = useState<any[]>([]);
  const [equipInfo, setEquipInfo] = useState<any[]>([]);

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const getFormCurrent = (key: string): SnFormRef => {
    const form = formRefs.current[key]?.current;
    if (!form) {
      throw new Error(`Form '${key}' is not mounted yet`);
    }
    return form;
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setFormDataSources({ freeform1: [] });

        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });

        setFormControls({
          freeform1: rtn.form.freeform1,
        });

        // SOP 버튼(btnSOP)은 MAS8025가 EICT인 경우에만 표출
        const grid1Header = rtn.header.grid1 || { controls: [], data: {} };
        const grid1HeaderFiltered =
          globalConfig?.MAS8025?.dc_rmk === "EICT"
            ? grid1Header
            : {
                ...grid1Header,
                controls: (grid1Header.controls || []).filter(
                  (ctrl: any) => ctrl.cd_param !== "btnSOP"
                ),
              };

        setHeaderInfo({
          grid1: grid1HeaderFiltered,
          grid2: rtn.header.grid2,
          grid3: rtn.header.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  // 입출고현황 등에서 수불번호로 드릴다운 — 이 화면은 작업지시 단위로만 조회하므로 작업지시번호를 찾아 바로 조회한다.
  // 실적번호(no_run) 그대로·no_run+"_R" 인 수불은 prd_run_chem 으로, 투입 수불은 mtl_qtio.no_order 로 찾는다.
  useEffect(() => {
    if ((Menuprops.figure as string) !== "fg_drilldown.Search") return;
    if (columns.grid1.length === 0) return;
    const no_io = Menuprops.nodeData?.no_io;
    if (!no_io) return;
    (async () => {
      const rows: any = await fnGetTableById({
        id: "drilldown_run_chem_wo",
        params: { no_io },
      });
      const no_wo = Array.isArray(rows) ? rows[0]?.no_wo : undefined;
      if (!no_wo) {
        await showDialog(
          "alter",
          `수불번호 ${no_io}의 작업지시를 찾을 수 없습니다.`
        );
        return;
      }
      await fetchByWo(no_wo);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetch = async () => {
      const [bowl, equip] = await Promise.all([
        fnGetTableById({
          id: "codel",
          params: { cd_firm: userInfo.cd_firm, cd_clases: ["PRD737"] },
        }),
        fnGetTableById({
          id: "equip",
          params: { cd_firm: userInfo.cd_firm, cd_biz: userInfo.cd_biz },
        }),
      ]);

      setMasCodeBowls(bowl);
      setEquipInfo(equip);
    };
    fetch();
  }, []);

  useEffect(() => {
    if (!recipeNodeData) return;

    const cdGrpEquip =
      getGridCurrent("grid1").focusedRowNode().data.cd_grp_equip;
    if (tpRecipe.current !== recipeNodeData.tp_recipe) {
      tpRecipe.current = recipeNodeData.tp_recipe;
    } else {
      return;
    }

    const virtual = sortColums(columns.grid2, tpRecipe.current);
    getGridCurrent("grid2").changeColumnReorderConfigs(virtual);

    let visibleGrid3Columns: string[] = [
      "nm_param",
      "dc_value_plan",
      "dc_value_run",
    ];
    let visibleGrid2Component: string[] = [""];

    switch (cdGrpEquip) {
      case "200": // 저울
        switch (tpRecipe.current) {
          case "cons": // 투입
            visibleGrid3Columns = ["no_lot", "no_lot_cust", "qt_use"];
            visibleGrid2Component = ["btnEnd"];
            break;
          case "recal": // 계근
            visibleGrid3Columns = ["qt"];
            visibleGrid2Component = [
              "cd_equip",
              "btnEnd",
              "btnEquip",
              "btnManual",
            ];
            break;
          case "pack": // 포장
            visibleGrid3Columns = ["no_lot", "qt"];
            visibleGrid2Component = [
              "cd_equip",
              "btnEnd",
              "btnEquip",
              "btnManual",
            ];
            break;
        }
        break;
      case "100": // 교반기
      case "300": // 밀링기
        visibleGrid3Columns = [
          "nm_param",
          "dc_value_plan",
          "dc_value_run",
          "tp_grpparam",
        ];
        visibleGrid2Component = ["cd_equip", "btnStart", "btnEquip"];
        break;
    }

    // 설비 컨트롤이 보이는 경우에만 grid3 수정 시 설비 선택 통제
    equipVisible.current = visibleGrid2Component.includes("cd_equip");

    const config: ChangeColumnConfig[] = columns.grid3.map((col: any) => ({
      cd_column: col.cd_column,
      nm_column: col.nm_column,
      fg_visible: visibleGrid3Columns.includes(col.cd_column) ? "Y" : "N",
    }));
    getGridCurrent("grid3").changeColumnConfigs(config);

    setHeaderInfo((prev: any) => {
      // 기존 데이터 구조가 무너지지 않도록 방어 코드 추가
      if (!prev || !prev.grid2 || !prev.grid3) return prev;
      const headerControls2 = prev.grid2.controls.map((ctrl: any) => ({
        ...ctrl,
        dc_editcon:
          ctrl.cd_param === "cd_equip"
            ? tmStart.current
              ? "true"
              : ""
            : ctrl.dc_editcon,
        fg_visible: visibleGrid2Component.includes(ctrl.cd_param) ? "Y" : "N",
      }));
      const headerControls3 = prev.grid3.controls.map((ctrl: any) => {
        // cdGrpEquip이 '200'이고 cons가 아닐 때는 무조건 'N'
        let fg_visible = "N";
        if (
          (cdGrpEquip === "200" && tpRecipe.current === "cons") ||
          tpRecipe.current === "filter"
        ) {
          fg_visible = ctrl.cd_param !== "dc_ref" ? "Y" : "N";
        } else if (tpRecipe.current === "pack") {
          fg_visible =
            ctrl.cd_param === "dc_ref" || ctrl.cd_param === "btnDel"
              ? "Y"
              : "N";
        } else if (tpRecipe.current === "recal") {
          fg_visible = ctrl.cd_param === "btnDel" ? "Y" : "N";
        } else if (cdGrpEquip !== "200") {
          fg_visible =
            ctrl.cd_param !== "no_barcode" &&
            ctrl.cd_param !== "dc_ref" &&
            ctrl.cd_param !== "btnDel"
              ? "Y"
              : "N";
        }

        // 26.06.26 삭제버튼 없애달라는데 롤백시킬 소지있어서
        if (ctrl.cd_param === "btnDel") {
          fg_visible = "N";
        }

        return { ...ctrl, fg_visible };
      });
      return {
        ...prev,
        grid2: {
          controls: headerControls2,
          data: {
            ...prev.grid2.data,
            cd_grp_equip: cdGrpEquip,
            cd_equip: tmStart.current ? cdEquip.current : "",
          },
        },
        grid3: {
          ...prev.grid3,
          controls: headerControls3,
        },
      };
    });
  }, [recipeNodeData]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    // onDelete,
    onSave,
    onPrint,
  }));

  //#region onSearch
  const onSearch = async () => {
    const modifiedRows = getGridCurrent("grid3").getChange();
    if (modifiedRows.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }

    // 이미 조회된 작업지시가 있으면 재조회 여부 확인
    const currentNoWo = getFormCurrent("freeform1").getFormdata()?.no_wo;
    if (currentNoWo) {
      const confirm = await showDialog(
        "confirm",
        "조회된 작업지시가 있습니다.\n재조회하시겠습니까?"
      );
      if (confirm) {
        await fetchByWo(currentNoWo);
        return;
      }
    }

    const component = <SN_PRD_RUN_REG_001_CHEM_POP_001 />;
    const result = await showResizablePopupModal({
      title: "작업지시도움",
      children: component,
    });
    if (!result || result === "close") return;

    await fetchByWo(result[0].no_wo);
  };

  const fetchByWo = async (noWo: string) => {
    const params = {
      id: "sn_prd_run_reg_001_chem_01",
      params: {
        cd_firm: userInfo.cd_firm,
        no_wo: noWo,
      },
    };

    const rtn: any = await fnGetDataSet(params);

    resetData();
    setFormDataSources({ freeform1: rtn[0][0] });
    setDataSources({
      grid1: rtn[1],
      grid2: rtn[2],
      grid3: rtn[3],
    });
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1")?.getDisplayedRowCount() < 1) return;

    const wo = getFormCurrent("freeform1").getFormdata();

    if (wo.st_wo === "X") {
      await showDialog("alter", "취소된 건입니다.");
      return;
    }

    const groupNode = getGridCurrent("grid1").focusedRowNode();
    if (!groupNode) return;

    if (groupNode.data.cd_grp_equip !== "200") {
      return;
    }

    if (groupNode.data.st_run === "C") {
      await showDialog("alter", "마감된 건입니다");
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    const nodesToDelete: any[] = [];
    getGridCurrent("grid3").forEachNode((node: RowNode) => {
      if (node.data.no_lot !== "") {
        nodesToDelete.push(node);
      }
    });
    nodesToDelete.forEach((node) => {
      getGridCurrent("grid3").deleteRowByNode(node);
    });

    getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
      node.setDataValue("st_run", "F");
    });

    return await onSave();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      if (getGridCurrent("grid1").getDisplayedRowCount() < 1) return;

      let hasInvalid = false;
      const currentIdx = getGridCurrent("grid1").focusedRowNode().rowIndex || 0;
      getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
        if (currentIdx > node.rowIndex! && node.data.st_run_before !== "C") {
          hasInvalid = true;
        }
      });

      if (hasInvalid) {
        await showDialog(
          "alter",
          "이전 작업그룹을 먼저 진행하지 않았거나 저장되지 않았습니다."
        );
        // resetData();
        return false;
      }

      const recipeNode = getGridCurrent("grid2").focusedRowNode();

      switch (recipeNode.data.tp_recipe) {
        case "mix":
        case "def":
        case "roll":
          if (recipeNode.data.st_run !== "C") {
            await showDialog("alter", "설비연동이 되지 않았습니다.");
            return false;
          }
          break;
      }

      const recipegroup = { insert: [], update: [] as any, delete: [] };
      const run = { insert: [] as any, update: [] as any, delete: [] };
      const runstep = {
        insert: [] as any,
        update: [] as any,
        delete: [] as any,
      };
      const runparam = {
        insert: [] as any,
        update: [] as any,
        delete: [] as any,
      };
      let cons = {
        insert: [] as any,
        update: [] as any,
        delete: [] as any,
      };
      let pack = {
        insert: [] as any,
        update: [] as any,
        delete: [] as any,
      };
      const woUpdate = { insert: [], update: [] as any, delete: [] };

      getGridCurrent("grid3").stopEditing();

      const requiredValuesCheck = getGridCurrent("grid3").requiredValuesCheck();
      if (!requiredValuesCheck) return false;
      const recipe = getGridCurrent("grid2").getChange();
      const paramChanges = getGridCurrent("grid3").getChange();

      if (
        recipe.modifiedRowcount === 0 &&
        paramChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const groupNode = getGridCurrent("grid1").focusedRowNode();
      groupNode.data.cd_emp = userInfo.cd_emp;
      groupNode.data.cd_dept = userInfo.cd_dept;
      groupNode.data.cd_gp = userInfo.cd_gp;
      recipegroup.update.push(groupNode.data);

      const wo = getFormCurrent("freeform1").getFormdata();

      let noSlip = converter.getString(wo.no_run);
      if (noSlip === "") {
        noSlip = converter.getString(
          await applySlip({
            cd_column: "no_run",
            cd_module: "PRD",
            no_line: 2,
            cd_prefix: "",
            dt: wo.dt_run,
            cd_menu: Menuprops.cd_menu,
            formrefs: [formRefs.current["freeform1"]],
          })
        );

        if (converter.getString(noSlip) === "") {
          await showDialog(
            "alter",
            "실적번호 채번에 실패했습니다.\n다시 시도해 주세요."
          );
          return false;
        }

        wo.dt_run = dateToString(new Date());
        wo.st_wo = "S";

        // 실적정보(선등록)
        wo.no_run = noSlip;
        wo.st_run = "S";
        run.insert.push(wo);
        woUpdate.update.push(wo);
      }

      getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
        if (
          node.data.tp_grpparam === "TEM" &&
          node.data.yn_input === "Y" &&
          !Number(converter.getString(node.data.dc_value_run))
        ) {
          hasInvalid = true;
        }
      });

      if (hasInvalid) {
        await showDialog(
          "alter",
          "온도값이 입력되지 않았거나 0인 항목이 있습니다."
        );
        return false;
      }

      switch (recipeNode.data.tp_recipe) {
        case "cons": // 투입
          cons = paramChanges;
          if (cons.insert.length > 0) {
            cons.insert.forEach((item: any) => {
              item.no_run = noSlip;
              item.fg_unit = "LOT";
            });
          }
          break;
        case "recal": // 계근
        case "pack": // 포장
          getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
            if (node.data.qt === 0) {
              hasInvalid = true;
            }
          });

          if (hasInvalid) {
            await showDialog("alter", "수량은 필수입니다.");
            return false;
          }

          pack = paramChanges;
          if (pack.insert.length > 0) {
            pack.insert.forEach((item: any) => {
              item.no_run = noSlip;
            });
          }
          break;
        case "inspect": // 검사
          getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
            if (node.data.yn_input === "Y") {
              if (
                node.data.dc_value_run === null ||
                node.data.dc_value_run === ""
              ) {
                hasInvalid = true;
              }
            }
          });

          if (hasInvalid) {
            await showDialog("alter", "입력되지 않은 검사 항목이 있습니다.");
            return false;
          }

          recipeNode.setDataValue("st_run", "C");
          recipegroup.update[0].st_run = "C";
          recipegroup.update[0].cd_emp = userInfo.cd_emp;
          recipegroup.update[0].nm_emp = userInfo.nm_emp;

          getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
            if (node.data.no_run === "") {
              node.data.no_run = noSlip;
              runparam.insert.push(node.data);
            } else {
              runparam.insert.push(node.data);
            }
          });
          break;
        default:
          // 26.09.07 kdy 수작업 공정(tp_recipe 없음): 체크 시 onCellValueChangedD 가 fnGetDate 응답을 기다린 뒤 st_run 을 C 로 바꾸는데
          // 그 사이 저장하면 그리드3 만 저장되고 그리드2/1 상태값이 이전 값으로 나가는 문제가 있어
          // 저장 시점에 상태값을 다시 계산해 강제로 세팅한다. (inspect 분기와 같은 방식)
          if (converter.getString(recipeNode.data.tp_recipe) === "") {
            let hasChecked = false;
            getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
              const value = converter.getString(node.data.dc_value_run);
              if (value !== "" && value !== "N") {
                hasChecked = true;
              }
            });

            if (hasChecked) {
              if (converter.getString(groupNode.data.tm_start) === "") {
                groupNode.data.tm_start = await fnGetDate();
              }
              recipeNode.setDataValue("st_run", "C", "noEvent");

              // 그리드2 전부 마감이면 작업그룹(그리드1)도 마감 + 종료시간
              let allClosed = true;
              getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
                if (node.data.st_run !== "C") {
                  allClosed = false;
                }
              });
              if (allClosed) {
                groupNode.data.st_run = "C";
                if (converter.getString(groupNode.data.tm_end) === "") {
                  groupNode.data.tm_end = await fnGetDate();
                }
              }
            }
          }

          getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
            if (node.data.no_run === "") {
              node.data.no_run = noSlip;
              runparam.insert.push(node.data);
            } else {
              runparam.insert.push(node.data);
            }
          });
          break;
      }

      // 투입실적없으면 저장안돼서 이렇게..
      runparam.insert.forEach((item: any) => {
        if (converter.getString(item.no_run) === "") {
          item.no_run = noSlip;
        }
      });

      getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
        if (node.data.fg_iud === "I") {
          node.data.no_run = noSlip;
          node.data.cd_wh = wo.cd_wh;
          runstep.insert.push(node.data);
        } else {
          node.data.cd_wh = wo.cd_wh;
          runstep.update.push(node.data);
        }
      });

      // 투입량이 상한값 초과하면
      let message = "";

      switch (recipeNode.data.tp_recipe) {
        case "cons":
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            if (node.data.qt_max < node.data.qt_use) {
              hasInvalid = true;
              message = `원료명: ${node.data.nm_matl}의 투입량이 상한값 보다 높아 작업지시 상태가 취소되었습니다.`;
              wo.st_wo = "X";
              // 실적정보
              wo.st_run = "X";
              run.update.push(wo);
              woUpdate.update.push(wo);
            }
          });
          break;
        case "inspect":
          getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
            if (node.data.yn_input === "Y") {
              // 최소/최대값이 모두 있는 경우
              if (
                node.data.dc_value_min !== "" &&
                node.data.dc_value_max !== "" &&
                node.data.dc_value_run !== null &&
                node.data.dc_value_run !== ""
              ) {
                if (
                  Number(node.data.dc_value_run) <
                    Number(node.data.dc_value_min) ||
                  Number(node.data.dc_value_run) >
                    Number(node.data.dc_value_max)
                ) {
                  hasInvalid = true;
                }
              }
              // 최소값만 있는 경우
              else if (
                node.data.dc_value_min !== "" &&
                node.data.dc_value_run !== null &&
                node.data.dc_value_run !== ""
              ) {
                if (
                  Number(node.data.dc_value_run) <
                  Number(node.data.dc_value_min)
                ) {
                  hasInvalid = true;
                }
              }
              // 최대값만 있는 경우
              else if (
                node.data.dc_value_max !== "" &&
                node.data.dc_value_run !== null &&
                node.data.dc_value_run !== ""
              ) {
                if (
                  Number(node.data.dc_value_run) >
                  Number(node.data.dc_value_max)
                ) {
                  hasInvalid = true;
                }
              }
            }

            if (hasInvalid) {
              message =
                "검사수치 범위를 벗어난 건이 있어 작업지시 상태가 취소되었습니다.";
              wo.st_wo = "X";
              // 실적정보
              wo.st_run = "X";
              run.update.push(wo);
              woUpdate.update.push(wo);
            }
          });

          break;
        case "pack":
          if (recipeNode.data.st_run === "C") {
            wo.st_wo = "C";
            let qt = 0;
            getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
              qt += node.data.qt;
            });
            // 실적정보
            wo.st_run = "C";
            wo.qt_run = qt;
            wo.qt_run_w = qt;
            run.update.push(wo);
            woUpdate.update.push(wo);
          }
          break;
      }

      const saveData = {
        recipegroup: recipegroup,
        run: run,
        runstep: runstep,
        runparam: runparam,
        cons: cons,
        pack: pack,
        wo: woUpdate,
      };

      const ok = await fnSave("SN_PRD_RUN_REG_001_CHEM_SAVE_01", saveData);
      if (!ok) {
        getFormCurrent("freeform1").resetUpdate(true);
        getGridCurrent("grid2").resetUpdate(true);
        getGridCurrent("grid3").resetUpdate(true);
        return false;
      }

      if (hasInvalid) {
        await showDialog("alter", message);
      }

      await fetchByWo(wo.no_wo);

      return true;
    } catch (error: any) {
      getFormCurrent("freeform1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region onPrint
  const onPrint = async () => {
    const recipeNode = getGridCurrent("grid2").focusedRowNode();

    if (!recipeNode) return;
    if (recipeNode.data.tp_recipe === "pack") {
      if (getGridCurrent("grid3").getDisplayedRowCount() !== 0) {
        const focusedRowNode = getGridCurrent("grid3").focusedRowNode();
        if (focusedRowNode) {
          const wo = getFormCurrent("freeform1").getFormdata();
          const printParams = {
            cd_firm: userInfo.cd_firm,
            dt_io: dateToString(new Date()),
            qt: focusedRowNode.data.qt.toString(),
            no_barcode: focusedRowNode.data.no_lot,
            cd_item: wo.cd_item,
            no_batch: wo.no_batch,
          };
          await fnPrintByReport("rpt_mtl_bas_reg_007_chem_02", printParams);
        }
      }
    }
  };
  //#endregion

  //#region GridH Events
  const onRowDataReadyH = () => {
    let idx = -1;
    getGridCurrent("grid1").forEachNode((node: RowNode) => {
      if (idx < 0) {
        if (node.data.st_run !== "C") {
          idx = node.rowIndex || 0;
        }
      }
    });

    if (idx >= 0) {
      // 다음 작업그룹으로 포커스 유지
      getGridCurrent("grid1").selectAndFocusRow(idx);
    } else {
      getGridCurrent("grid1").selectAndFocusRow(
        getGridCurrent("grid1").getDisplayedRowCount() - 1
      );
    }
  };
  //#endregion

  const onBtnEtcClickH = async (id: string) => {
    if (id === "btnSOP") {
      const focusedNodeH = gridRefs.current["grid1"].current?.focusedRowNode();
      if (!focusedNodeH) {
        await showDialog("alter", "작업그룹을 선택해주세요.");
        return;
      }

      const component = (
        <SN_PRD_RUN_REG_001_CHEM_POP_004
          selectedData={{ cd_group: focusedNodeH.data?.cd_group }}
        />
      );
      await showResizablePopupModal({
        title: "SOP 파일조회",
        children: component,
        width: "1200",
        height: "700px",
        okayBtn: false,
      });
    }
  };

  const onBtnEtcClickL = async (id: string) => {
    const wo = getFormCurrent("freeform1").getFormdata();

    if (wo.st_wo === "X") {
      await showDialog("alter", "취소된 건입니다.");
      return;
    }

    const recipeNode = getGridCurrent("grid2").focusedRowNode();

    if (recipeNode.data.st_run === "C") {
      await showDialog("alter", "마감된 건입니다");
      return;
    }

    let hasInvalid = false;
    const currentIdx = getGridCurrent("grid1").focusedRowNode().rowIndex || 0;
    getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
      if (currentIdx > node.rowIndex! && node.data.st_run_before !== "C") {
        hasInvalid = true;
      }
    });

    if (hasInvalid) {
      await showDialog(
        "alter",
        "이전 작업그룹을 먼저 진행하지 않았거나 저장되지 않았습니다."
      );
      return false;
    }

    const groupNode = getGridCurrent("grid1").focusedRowNode();
    const recipe = recipeNode.data;

    switch (id) {
      case "btnEquip":
        {
          if (cdEquip.current === "") {
            await showDialog("alter", "설비를 선택해주세요.");
            return;
          } else if (recipe.tp_recipe === "pack" && wo.no_batch === "") {
            await showDialog("alter", "Batch No를 입력해 주세요.");
            return;
          } else if (recipe.tp_recipe === "pack" && cdBowl.current === "") {
            await showDialog("alter", "용기를 선택해주세요.");
            return;
          } else if (
            (recipe.tp_recipe === "mix" ||
              recipe.tp_recipe === "def" ||
              recipe.tp_recipe === "roll") &&
            !tmStart.current
          ) {
            await showDialog("alter", "시작버튼을 먼저 눌러주세요.");
            return;
          }

          await setTmStart();

          let diffMins = 0;
          // 작업시간 체크
          if (tmStart.current) {
            const tmNow = new Date();

            let tmMinDiff = 0,
              tmMaxDiff = 0;
            getGridCurrent("grid3").forEachFilteredNode((node) => {
              switch (node.rowIndex) {
                case 1:
                  tmMinDiff = Number(node.data.dc_value_plan);
                  break;
                case 2:
                  tmMaxDiff = Number(node.data.dc_value_plan);
                  break;
              }
            });

            const timeDifferenceMs =
              tmNow.getTime() - tmStart.current.getTime();
            const actualMinutesDiff = Math.floor(
              timeDifferenceMs / (1000 * 60)
            );
            const totalMinutesDiff = (actualMinutesDiff - tmMinDiff) * -1;

            // 교반기일 경우만 시간 예외처리 및 알림
            if (recipe.tp_recipe === "mix" || recipe.tp_recipe === "def") {
              if (totalMinutesDiff > 1) {
                await showDialog("alter", "작업최소시간이 지나지 않았습니다.");
                return;
              }

              if (tmMaxDiff < actualMinutesDiff) {
                await showDialog("alter", "작업최대시간이 지났습니다.");
              }
            }

            if (tmStart.current !== undefined) {
              const tmEnd = new Date();
              const diffMs = tmEnd.getTime() - tmStart.current.getTime();
              diffMins = Math.floor(diffMs / 60000);
            }
          }

          const rtn = await apiPost("/SN_PRD_RUN_REG_001_CHEM_READ", {
            equip: cdEquip.current,
            grpEquip: groupNode.data.cd_grp_equip,
          });

          if (rtn.status !== "S") {
            return;
          }

          // 연동 설비정보 넣어주기
          recipeNode.data.cd_equip = cdEquip.current;

          if (recipe.tp_recipe === "recal") {
            if (getGridCurrent("grid3").getDisplayedRowCount() > 0) {
              await showDialog(
                "alter",
                "이미 연동되거나 등록된 데이터가 있습니다."
              );
              return;
            }
            let value = Math.abs(rtn.data?.qt);

            if (value == 0) {
              value = 0;
            } else {
              value =
                cdEquip.current === "W001" || cdEquip.current === "W004"
                  ? value / 100
                  : value;
            }

            const newRow = {
              cd_firm: userInfo.cd_firm,
              no_lot: "",
              qt: value,
              no_run: wo.no_run,
              no_wo: wo.no_wo,
              no_line: 1,
              no_line_step: recipe.no_line,
              cd_group: recipeNode.data.cd_group,
            };
            getGridCurrent("grid3").addRow(newRow);
          } else if (recipe.tp_recipe === "pack") {
            let value = Math.abs(rtn.data?.qt);

            if (value === 0) {
              await showDialog("alter", "저울에서 가져온 데이터가 0입니다.");
              return;
            } else {
              value =
                cdEquip.current === "W001" || cdEquip.current === "W004"
                  ? value / 100
                  : value;
            }

            const maxNoLine = getGridCurrent("grid3").getMaxValue("no_line");

            if (value > 0) {
              const bowlData = masCodeBowls.find(
                (d: any) => d["cd_flag"] === cdBowl.current
              );

              let bowlSize = Number(bowlData?.dc_ref);

              if (bowlData?.cd_flag === "PW999") {
                // 아래저울 2개만 소수점 2자리사용
                let decimalPoint = 0;
                switch (cdEquip.current) {
                  case "W001":
                  case "W004":
                    decimalPoint = 2;
                    break;
                }
                const component = (
                  <NumPadPopup
                    defaultValue={bowlSize || 0}
                    decimalPoint={decimalPoint}
                  />
                );
                const numpadResult = await showResizablePopupModal({
                  title: "용기무게 입력",
                  children: component,
                  height: "600px",
                  width: "500px",
                });

                if (!numpadResult || numpadResult === "close") return;

                bowlSize = Number(numpadResult[0]);
              }

              value -= bowlSize;
            }

            const noBsn = await getBsn(
              "NOLOTCUST",
              wo.no_batch,
              "00000000",
              1,
              1,
              "Y"
            );
            const woSeq = String(noBsn.no_serial).padStart(3, "0");
            const resultBsn =
              wo.cd_grpitem + wo.cd_dummy02 + wo.no_batch + "A" + woSeq;

            const newRow = {
              cd_firm: userInfo.cd_firm,
              no_lot: resultBsn,
              qt: value,
              no_run: wo.no_run,
              no_wo: wo.no_wo,
              no_line: maxNoLine + 1,
              no_line_step: recipe.no_line,
              cd_group: recipeNode.data.cd_group,
            };
            getGridCurrent("grid3").addRow(newRow);

            const printParams = {
              cd_firm: userInfo.cd_firm,
              dt_io: dateToString(new Date()),
              qt: newRow.qt.toString(),
              no_barcode: newRow.no_lot,
              cd_item: wo.cd_item,
              no_batch: wo.no_batch,
            };
            await fnPrintByReport("rpt_mtl_bas_reg_007_chem_02", printParams);
          } else {
            getGridCurrent("grid3").forEachFilteredNode((node) => {
              const seq = node.data.no_seq_equip;
              let value = Math.abs(rtn.data?.dc_value_run[seq || 0]);

              if (seq > 0) {
                node.setDataValue("dc_value_run", value == 0 ? 0 : value / 10);
              }

              if (
                node.data.tp_grpparam === "TIM" &&
                node.data.yn_input === "Y"
              ) {
                node.setDataValue("dc_value_run", diffMins.toString());
              }
            });
            recipeNode.setDataValue("st_run", "C");
          }
          recipeNode.setDataValue("tm_start", rtn.data?.tm_start);
          recipeNode.setDataValue("tm_end", rtn.data?.tm_end);

          // 타이머 종료
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            isWarned.current = false;
          }
        }
        break;
      case "btnManual":
        switch (recipe.tp_recipe) {
          case "recal":
            {
              if (getGridCurrent("grid3").getDisplayedRowCount() > 0) {
                await showDialog(
                  "alter",
                  "이미 연동되거나 등록된 데이터가 있습니다."
                );
                return;
              } else {
                const component = (
                  <NumPadPopup defaultValue={0} decimalPoint={2} />
                );
                const result = await showResizablePopupModal({
                  title: "",
                  children: component,
                  height: "600px",
                  width: "500px",
                });

                if (!result || result === "close") return;

                const value = result[0];

                if (Number(value) === 0) {
                  await showDialog("alter", "수량이 0입니다.");
                  return;
                }

                const newRow = {
                  cd_firm: userInfo.cd_firm,
                  no_lot: "",
                  qt: value,
                  no_run: wo.no_run,
                  no_wo: wo.no_wo,
                  no_line: 1,
                  no_line_step: recipe.no_line,
                  cd_group: recipeNode.data.cd_group,
                };
                getGridCurrent("grid3").addRow(newRow);
              }
            }
            break;
          case "pack":
            {
              if (cdEquip.current === "") {
                await showDialog("alter", "설비를 선택해주세요.");
                return;
              }

              const equipData = equipInfo.find(
                (d: any) => d["cd_equip"] === cdEquip.current
              );
              if (equipData.yn_sync !== "N") {
                await showDialog(
                  "alter",
                  "미연동 설비만 수동 입력 가능합니다."
                );
                return;
              }

              const component = (
                <NumPadPopup defaultValue={0} decimalPoint={2} />
              );
              const result = await showResizablePopupModal({
                title: "",
                children: component,
                height: "600px",
                width: "500px",
              });

              if (!result || result === "close") return;

              const value = result[0];

              if (Number(value) === 0) {
                await showDialog("alter", "수량이 0입니다.");
                return;
              }

              recipeNode.setDataValue("cd_equip", cdEquip.current);

              let noSeq = "";

              const noBsn = await getBsn(
                "NOLOTCUST",
                wo.no_batch,
                "00000000",
                1,
                1,
                "Y"
              );
              noSeq = String(noBsn.no_serial).padStart(3, "0");

              const resultBsn =
                wo.cd_grpitem + wo.cd_dummy02 + wo.no_batch + "A" + noSeq;
              const maxNoLine = getGridCurrent("grid3").getMaxValue("no_line");

              const newRow = {
                cd_firm: userInfo.cd_firm,
                no_lot: resultBsn,
                qt: value,
                no_run: wo.no_run,
                no_wo: wo.no_wo,
                no_line: maxNoLine + 1,
                no_line_step: recipe.no_line,
                cd_group: recipeNode.data.cd_group,
              };
              getGridCurrent("grid3").addRow(newRow);

              const printParams = {
                cd_firm: userInfo.cd_firm,
                dt_io: dateToString(new Date()),
                qt: newRow.qt.toString(),
                no_barcode: newRow.no_lot,
                cd_item: wo.cd_item,
                no_batch: wo.no_batch,
              };
              await fnPrintByReport("rpt_mtl_bas_reg_007_chem_02", printParams);
            }
            break;
        }
        break;
      case "btnStart":
        if (cdEquip.current === "") {
          await showDialog("alter", "설비를 선택해주세요.");
          return;
        }

        // 26.09.18 kdy 미연동 설비는 시작 불가
        const equipData = equipInfo.find(
          (d: any) => d["cd_equip"] === cdEquip.current
        );
        if (equipData?.yn_sync === "N") {
          await showDialog("alter", "미연동 설비입니다.");
          return;
        }

        if (
          intervalRef.current !== null ||
          (recipe.tp_recipe === "roll" && tmStart.current)
        ) {
          await showDialog("alter", "진행중입니다.");
          return;
        }

        if (tmStart.current) {
          await showDialog("alter", "이미 설비와 연동된 데이터가 있습니다.");
          return;
        }

        await setTmStart();

        let tmMinDiff = 0,
          tmMaxDiff = 0;
        getGridCurrent("grid3").forEachFilteredNode((node) => {
          if (node.data.tp_grpparam === "TIM") {
            switch (node.rowIndex) {
              case 1:
                tmMinDiff = Number(node.data.dc_value_plan);
                break;
              case 2:
                tmMaxDiff = Number(node.data.dc_value_plan);
                break;
            }
          }
        });

        // 26.09.18 kdy 콜백으로 받아 공통 다이얼로그 대신 화면에서 직접 처리
        // - 응답을 못 받은 경우(네트워크 순단, 프록시 오류)는 프론트 폴백(SERVER_ERROR)으로 들어오므로 구분해서 안내
        const rtn = await apiPost(
          "/SN_PRD_RUN_REG_001_CHEM_START",
          {
            equip: cdEquip.current,
            grpEquip: groupNode.data.cd_grp_equip,
          },
          async (resp) => {
            if (resp.status === "S") return;

            if (resp.status === "W" || resp.status === "F") {
              await showDialog("warning", resp.message);
              return;
            }

            // 서버 응답 자체를 못 받은 경우 (fetch 실패 / 비JSON 응답)
            if (resp.error?.errorCode === "SERVER_ERROR") {
              await showDialog(
                "error",
                "설비 시작 응답을 받지 못했습니다.\n네트워크 상태를 확인하고 다시 시도해 주세요."
              );
              return;
            }

            await showDialog("error", resp.message);
          }
        );

        if (rtn.status === "S") {
          // 교반기일 경우 타이머
          if (recipe.tp_recipe === "mix" || recipe.tp_recipe === "def") {
            startTimer(tmMinDiff, tmMaxDiff);
          }
          // 밀링기일 경우 0부터 시작하는 타이머
          else {
            tmStart.current = new Date();
            startTimer2();
            showDialog("success", "시작되었습니다.");
          }
        }
        break;
      case "btnEnd":
        // 투입인 경우 투입량하한값 체크
        if (recipeNode.data.tp_recipe === "cons") {
          let hasInvalid = false;
          let message = "";
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            if (node.data.qt_min > node.data.qt_use) {
              hasInvalid = true;
              message = `원료명: ${node.data.nm_matl}의 투입량이 하한값 보다 낮습니다.`;
            }
          });

          if (hasInvalid) {
            await showDialog("alter", message);
            return;
          }
        }
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue("st_run", "C");
        });
        break;
    }
  };

  const onCellValueChangedL = async (data: CellValueChangedEvent) => {
    switch (data.colId) {
      case "st_run":
        let hasInvalid = true;
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (node.data.st_run !== "C") {
            hasInvalid = false;
          }
        });

        // 모든 상태 마감이면 작업종료시간 넣어주기
        if (hasInvalid) {
          const groupNode = getGridCurrent("grid1").focusedRowNode();
          groupNode.setDataValue("st_run", "C", "noEvent");
          groupNode.setDataValue("cd_emp", userInfo.cd_emp, "noEvent");
          groupNode.setDataValue("nm_emp", userInfo.nm_emp, "noEvent");
          if (converter.getString(groupNode.data.tm_end) === "") {
            const tmEnd = await fnGetDate();
            groupNode.setDataValue("tm_end", tmEnd, "noEvent");
          }
        }
    }
  };

  const onFocusedRowChangedL = async (data: any) => {
    setRecipeNodeData(data);

    let filterKey = "no_line"; // 기본 필터 키
    if (data.cd_grp_equip === "200") {
      // 저울
      filterKey = "no_line_step";
    }
    getGridCurrent("grid3").applyFilters({
      cd_group: data.cd_group,
      [filterKey]: data.no_line, // 동적 키 적용
    });
  };

  //#endregion

  //#region GridD Events
  const onCellClickedD = async (params: any) => {
    const recipeNode = getGridCurrent("grid2").focusedRowNode();
    if (
      recipeNode.data.st_run !== "C" /*recipeNode.data.tp_recipe === "recal" ||
        recipeNode.data.tp_recipe === "pack" ||*/ && // 26.07.22 계근, 포장 수정 못하게
      recipeNode.data.tp_recipe === "inspect"
    ) {
      // 이전 작업그룹 진행 해야만 되게
      let hasInvalid = false;
      const currentIdx = getGridCurrent("grid1").focusedRowNode().rowIndex || 0;
      getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
        if (currentIdx > node.rowIndex! && node.data.st_run !== "C") {
          hasInvalid = true;
        }
      });

      if (hasInvalid) {
        return;
      }

      let decimalPoint = 0;

      const recipeValue = getGridCurrent("grid3").focusedRowNode();

      if (
        recipeNode.data.tp_recipe === "inspect" &&
        recipeValue.data.yn_input !== "Y"
      ) {
        return;
      }

      // 아래저울 2개만 소수점 2자리사용
      switch (cdEquip.current) {
        case "W001":
        case "W004":
          decimalPoint = 2;
          break;
      }

      switch (recipeNode.data.tp_recipe) {
        case "inspect":
          decimalPoint = 1;
          break;
      }

      const component = (
        <NumPadPopup
          defaultValue={params.node.data.qt}
          decimalPoint={decimalPoint}
        />
      );
      const result = await showResizablePopupModal({
        title: "",
        children: component,
        height: "600px",
        width: "500px",
      });

      if (!result || result === "close") return;

      if (recipeNode.data.tp_recipe === "inspect") {
        params.node.setDataValue("dc_value_run", result[0]);
      } else {
        params.node.setDataValue("qt", result[0], "noEvent");
      }
    }
  };

  const onCellValueChangedD = async (data: CellValueChangedEvent) => {
    const recipeNode = getGridCurrent("grid2").focusedRowNode();

    // 설비 컨트롤이 보이는 화면에서 설비 미선택 시 수정 불가
    if (equipVisible.current && cdEquip.current === "") {
      await showDialog("alter", "설비를 선택해주세요.");
      data.rowNode.setDataValue(data.colId, data.oldValue, "noEvent");
      return;
    }
    switch (data.colId) {
      case "qt_use":
        const sum = getGridCurrent("grid3").getFooterData();
        if (sum.qt_use + data.newValue > recipeNode.data.qt_min) {
          recipeNode.setDataValue("st_run", "C");
        } else {
          recipeNode.setDataValue("st_run", "S");
        }
        break;
      case "dc_value_run":
        await setTmStart();

        // 26.09.09 kdy 미연동 설비면 상태값 변경
        if (cdEquip.current !== "") {
          const equipData = equipInfo.find(
            (d: any) => d["cd_equip"] === cdEquip.current
          );
          if (equipData.yn_sync === "N") {
            recipeNode.setDataValue("cd_equip", cdEquip.current);
            recipeNode.setDataValue("st_run", "C");
          }
        }

        // 밀링기는 체크박스 있어서
        switch (recipeNode.data.tp_recipe) {
          case "roll":
            break;
          case "inspect": {
            // 26.09.17 kdy 검사공정 계산로직 점도 입력시 T.I값 자동 계산
            // CP: QC057 / QC004 = QC010,  SP: QC058 / QC036 = QC010
            const TI_PAIRS: Record<string, { num: string; den: string }> = {
              QC057: { num: "QC057", den: "QC004" }, // T.I 측정용 점도값CP
              QC004: { num: "QC057", den: "QC004" }, // 점도CP
              QC058: { num: "QC058", den: "QC036" }, // T.I 측정용 점도값SP
              QC036: { num: "QC058", den: "QC036" }, // 점도SP
            };
            const pair = TI_PAIRS[data.rowData.cd_param];
            if (!pair) break;

            const values: Record<string, number> = {};
            let tiNode: RowNode | undefined;
            // 빈값은 NaN, "4,900" 같은 콤마 문자열은 숫자로
            const toNum = (v: unknown) => {
              const s = converter.getString(v).replace(/,/g, "").trim();
              return s === "" ? NaN : Number(s);
            };

            getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
              const cd = node.data.cd_param;
              if (cd === pair.num || cd === pair.den) {
                values[cd] = toNum(node.data.dc_value_run);
              } else if (cd === "QC010") {
                tiNode = node;
              }
            });

            if (tiNode) {
              const num = values[pair.num];
              const den = values[pair.den];
              // 소수 2자리 반올림
              const calcValue =
                Number.isFinite(num) && Number.isFinite(den) && den !== 0
                  ? String(Math.round((num / den) * 100) / 100)
                  : "";
              tiNode.setDataValue("dc_value_run", calcValue, "noEvent");
            }
            break;
          }
          default:
            recipeNode.setDataValue("st_run", "C");
            break;
        }
        break;
    }
  };

  const onBtnEtcClickD = async (id: string) => {
    switch (id) {
      case "btnDel":
        const recipeNode = getGridCurrent("grid2").focusedRowNode();
        const focusedRowNode = getGridCurrent("grid3").focusedRowNode();

        if (!focusedRowNode) return;

        if (recipeNode.data.tp_recipe === "cons") {
          if (recipeNode.data.st_run === "C") {
            await showDialog("alter", "마감된 건입니다.");
          } else {
            recipeNode.setDataValue(
              "qt_use",
              recipeNode.data.qt_use - focusedRowNode.data.qt_use,
              "noEvent"
            );
            if (recipeNode.data.qt_use === 0) {
              recipeNode.setDataValue("st_run", "F");
            } else {
              recipeNode.setDataValue("st_run", "S");
            }
            getGridCurrent("grid3").deleteSelectedRows();
          }
        } else {
          if (recipeNode.data.st_run === "C") {
            await showDialog("alter", "마감된 건입니다.");
          } else if (focusedRowNode.data.state !== "insert") {
            await showDialog("alter", "신규건만 삭제할 수 있습니다.");
          } else {
            getGridCurrent("grid3").deleteSelectedRows();
          }
        }
        break;
    }
  };
  //#endregion

  //#region Form Events
  const onBtnEtcClickFreeForm = async (id: string) => {
    const wo = getFormCurrent("freeform1").getFormdata();
    if (!wo) return;

    switch (id) {
      case "btnCheck":
        break;
      case "btnInfo":
        {
          if (converter.getString(wo.txt_item_spec) === "") {
            await showDialog("alter", "세부사항 정보가 없습니다.");
            return;
          }

          const component = (
            <SN_PRD_RUN_REG_001_CHEM_POP_003 txtItemSpec={wo.txt_item_spec} />
          );
          await showResizablePopupModal({
            title: "세부사항",
            children: component,
            height: "600px",
            width: "800px",
            okayBtn: false,
            closeBtnText: "닫기",
          });
        }
        break;
      case "btnRmk":
        {
          const form = getFormCurrent("freeform1");

          const wo = form.getFormdata();
          if (!wo || wo.length < 1) return;

          const component = (
            <SN_PRD_RUN_REG_001_CHEM_POP_005 txtRmk={wo.txt_rmk} />
          );
          const result = await showResizablePopupModal({
            title: "특이사항",
            children: component,
            height: "600px",
            width: "800px",
          });

          if (!result || result === "close") return;

          // txt_rmk는 텍스트 컬럼이라 문자열로 넣어준다
          const txtRmk = converter.getString(result[0]);

          const ok = await fnSave("SN_PRD_RUN_REG_001_CHEM_SUB_02", {
            wo: {
              insert: [],
              update: [{ ...wo, txt_rmk: txtRmk }],
              delete: [],
            },
          });
          if (!ok) return;

          // 이미 저장된 값이라 수정행으로는 잡지 않는다
          await form.setDataValue("txt_rmk", txtRmk, "noChange");
        }
        break;
      case "btnEdit":
        {
          const form = getFormCurrent("freeform1");

          const wo = form.getFormdata();
          if (!wo || wo.length < 1) return;

          const component = <NumPadPopup defaultValue={0} decimalPoint={0} />;
          const result = await showResizablePopupModal({
            title: "",
            children: component,
            height: "600px",
            width: "500px",
          });

          if (!result || result === "close") return;

          // no_batch는 텍스트 컬럼이라 문자열로 넣어준다
          const noBat = String(result[0]);

          // 배치번호 중복체크 및 저장은 서버에서 처리한다
          const ok = await fnSave("SN_PRD_RUN_REG_001_CHEM_SUB_01", {
            wo: {
              insert: [],
              update: [{ ...wo, no_batch: noBat }],
              delete: [],
            },
          });
          if (!ok) return;

          // 이미 저장된 값이라 수정행으로는 잡지 않는다
          await form.setDataValue("no_batch", noBat, "noChange");
        }
        break;
    }
  };

  const onControlFocusFreeForm = async (param: string) => {
    if (param !== "no_batch") return;
    if (isNumPadOpen.current) return;

    const form = getFormCurrent("freeform1");
    if (form.getIsReadOnly("no_batch")) return;

    const wo = form.getFormdata();
    if (!wo || wo.length < 1) return;

    isNumPadOpen.current = true;
    try {
      // 포커스를 빼고 띄워야 팝업이 닫힐 때 포커스가 되돌아와 다시 열리지 않는다
      (document.activeElement as HTMLElement)?.blur?.();

      const component = <NumPadPopup defaultValue={0} decimalPoint={0} />;
      const result = await showResizablePopupModal({
        title: "",
        children: component,
        height: "600px",
        width: "500px",
      });

      if (!result || result === "close") return;

      // no_batch는 텍스트 컬럼이라 문자열로 넣어준다
      const noBat = String(result[0]);

      // 배치번호 중복체크 및 저장은 서버에서 처리한다
      const ok = await fnSave("SN_PRD_RUN_REG_001_CHEM_SUB_01", {
        wo: {
          insert: [],
          update: [{ ...wo, no_batch: noBat }],
          delete: [],
        },
      });
      if (!ok) return;

      // 이미 저장된 값이라 수정행으로는 잡지 않는다
      await form.setDataValue("no_batch", noBat, "noChange");
    } catch (error: any) {
    } finally {
      isNumPadOpen.current = false;
    }
  };
  //#endregion

  const onEnter = async (sender: string, value: string) => {
    if (sender === "no_barcode") {
      const wo = getFormCurrent("freeform1").getFormdata();
      if (!wo) return;
      if (wo.st_wo === "X") {
        await showDialog("alter", "취소된 건입니다.");
        return;
      }

      const recipeNode = getGridCurrent("grid2").focusedRowNode();

      if (!recipeNode) return;

      let hasInvalid = false;
      const currentIdx = getGridCurrent("grid1").focusedRowNode().rowIndex || 0;
      getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
        if (currentIdx > node.rowIndex! && node.data.st_run_before !== "C") {
          hasInvalid = true;
        }
      });

      if (hasInvalid) {
        await showDialog(
          "alter",
          "이전 작업그룹을 먼저 진행하지 않았거나 저장되지 않았습니다."
        );
        return false;
      }

      const groupNode = getGridCurrent("grid1").focusedRowNode();
      if (groupNode.data.st_run === "C") {
        await showDialog("alter", "완료된 건입니다.");
        return false;
      }

      await setTmStart();

      switch (recipeNode.data.tp_recipe) {
        case "cons": // 투입
          const itemList: any = [];

          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            itemList.push({
              cd_biz: userInfo.cd_biz,
              cd_wh: wo.cd_wh,
              no_barcode: value,
              cd_loc: "",
              cd_item: node.data.cd_matl,
            });
          });

          const searchLotData = await fnGetwhLocLotMultiItemScan(itemList);

          if (searchLotData.length < 1) {
            await showDialog(
              "alter",
              "재고가 없거나 투입품목과 일치하는 LOT정보를 찾을 수 없습니다."
            );
            SetFocus();
          } else {
            // 스캔LOT 포커스 잡아주기
            const cdMatl = searchLotData[0].cd_item;
            const noLotCust = searchLotData[0].no_lot_cust;

            let focusedRowIdx = 0;
            let focusedRecipeData: any;
            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              if (node.data.cd_matl === cdMatl) {
                focusedRowIdx = node.rowIndex ?? focusedRowIdx;
                focusedRecipeData = node.data;
              }
            });

            getGridCurrent("grid2").selectAndFocusRow(focusedRowIdx);

            let isScanded = false;

            getGridCurrent("grid3").forEachNode((node: RowNode) => {
              if (node.data.state === "insert" && node.data.no_lot === value) {
                isScanded = true;
              }
            });

            if (isScanded) {
              await showDialog("alter", "이미 투입한 LOT정보입니다.");
              return;
            }

            const lotList: any[] = [];
            getGridCurrent("grid3").forEachNode((node: RowNode) => {
              if (focusedRecipeData.no_line === node.data.no_line_step) {
                lotList.push(node.data);
              }
            });

            const component = (
              <SN_PRD_RUN_REG_001_CHEM_POP_002
                recipe={focusedRecipeData}
                cdWh={wo.cd_wh}
                barcode={value}
                noLotCust={noLotCust}
                qtInv={searchLotData[0].qt_inv}
                lotList={lotList}
                recentEquip={recentPopupEquip.current}
              />
            );
            const result = await showResizablePopupModal({
              title: "LOT도움",
              children: component,
              height: "580px",
              width: "1200px",
              checkData: false,
              validation: true,
            });

            SetFocus();

            if (!result || result === "close") return;

            // 선택했던 설비(저울) 기억
            recentPopupEquip.current = result[0].cd_equip;

            const recipeNode = getGridCurrent("grid2").focusedRowNode();
            const maxNoLine = getGridCurrent("grid3").getMaxValue("no_line");

            const newRow = {
              cd_firm: userInfo.cd_firm,
              no_lot: result[0].no_lot,
              no_lot_cust: result[0].no_lot_cust,
              cd_matl: result[0].cd_matl,
              qt_use: result[0].qt_use,
              no_run: wo.no_run,
              no_wo: wo.no_wo,
              no_line: maxNoLine + 1,
              no_line_step: recipeNode.data.no_line,
              cd_group: recipeNode.data.cd_group,
            };
            getGridCurrent("grid3").addRow(newRow);

            // 연동 설비정보 넣어주기
            recipeNode.data.cd_equip = result[0].cd_equip;

            if (recipeNode.data.qt_use >= recipeNode.data.qt_min) {
              recipeNode.setDataValue("st_run", "C");
            } else {
              recipeNode.setDataValue("st_run", "S");
            }

            // 상한값 초과시 알람
            let hasInvalid = false;
            let message = "";
            if (recipeNode.data.qt_max < recipeNode.data.qt_use) {
              hasInvalid = true;
              message = `원료명: ${recipeNode.data.nm_matl}의 투입량이 상한값을 초과했습니다.`;
            }

            // 하한값 미만시 알람
            let hasInvalid2 = false;
            let message2 = "";
            if (
              recipeNode.data.qt_min > recipeNode.data.qt_use &&
              recipeNode.data.qt_use !== 0
            ) {
              hasInvalid2 = true;
              message2 = `원료명: ${recipeNode.data.nm_matl}의 투입량이 하한값 보다 낮습니다.`;
            }

            if (hasInvalid) {
              await showDialog("alter", message);
            } else if (hasInvalid2) {
              await showDialog("alter", message2);
            }

            SetFocus();
          }
          break;
        case "filter": // 여과
          getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
            if (node.data.nm_param === "Mesh") {
              if (converter.getString(node.data.dc_value_run) === "") {
                recipeNode.data.cd_equip = value;
                node.setDataValue("dc_value_run", value, "noEvent");
                recipeNode.setDataValue("st_run", "C");
              } else {
                hasInvalid = true;
              }
            }
          });

          if (hasInvalid) {
            await showDialog("alter", "이미 등록되었습니다.");
          }
          break;
      }
    }
  };

  //#region
  const setTmStart = async () => {
    const groupNode = getGridCurrent("grid1").focusedRowNode();

    if (converter.getString(groupNode.data.tm_start) === "") {
      const tmStart = await fnGetDate();
      groupNode.setDataValue("tm_start", tmStart, "noEvent");
    }
  };

  const resetData = (): any => {
    getFormCurrent("freeform1").resetUpdate();
    getGridCurrent("grid1").resetUpdate();
    getGridCurrent("grid2").resetUpdate();
    getGridCurrent("grid3").resetUpdate();
    cdEquip.current = "";
    tmStart.current = undefined;
    isWarned.current = false;
    cdBowl.current = "";
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;

    setHeaderInfo((prev: any) => {
      return {
        ...prev,
        grid2: {
          ...prev.grid2,
          data: {
            ...prev.grid2.data,
            cd_equip: "", // 설비값 초기화
          },
        },
        grid3: {
          ...prev.grid3,
          controls: prev.grid3.controls.map((ctrl: any) =>
            ctrl.cd_param === "timer"
              ? {
                  ...ctrl,
                  nm_label: "",
                }
              : ctrl
          ),
          data: {
            ...prev.grid3.data,
            dc_ref: "", // 용기값 초기화
          },
        },
      };
    });
  };

  const SetFocus = () => {
    const targetInput = document.querySelector(
      'input[id="BarcodeScan"]'
    ) as HTMLInputElement;
    if (targetInput) {
      targetInput.focus();
      targetInput.select?.();
    }
  };

  const startTimer = (minminutes: number, maxminutes: number) => {
    tmStart.current = new Date();

    // 기존에 돌고 있던 인터벌이 있다면 초기화
    if (intervalRef.current) clearInterval(intervalRef.current);

    const targetMs = maxminutes * 60 * 1000;
    let minMs = minminutes * 60 * 1000;
    let maxMs = maxminutes * 60 * 1000;

    setHeaderInfo((prev: any) => {
      return {
        ...prev,
        grid2: {
          ...prev.grid2,
          controls: prev.grid2.controls.map((ctrl: any) =>
            ctrl.cd_param === "cd_equip"
              ? {
                  ...ctrl,
                  dc_editcon: "true",
                }
              : ctrl
          ),
          data: {
            ...prev.grid2.data,
            cd_equip: cdEquip.current,
          },
        },
      };
    });

    // 1초마다 실시간으로 체크
    intervalRef.current = setInterval(() => {
      if (tmStart.current) {
        const diffInMs = new Date().getTime() - tmStart.current.getTime();
        const remainingMs = targetMs - diffInMs;

        setHeaderInfo((prev: any) => {
          return {
            ...prev,
            grid3: {
              ...prev.grid3,
              controls: prev.grid3.controls.map((ctrl: any) =>
                ctrl.cd_param === "timer"
                  ? {
                      ...ctrl,
                      nm_label: formatTime(remainingMs),
                    }
                  : ctrl
              ),
            },
          };
        });
        if (minMs !== 0 && diffInMs >= minMs) {
          showDialog("success", "작업최소시간을 경과했습니다.");
          minMs = 0;
        }

        if (remainingMs <= 60000 && !isWarned.current) {
          isWarned.current = true;
          showDialog("alter", "작업 종료까지 1분 남았습니다.");
        }
        if (diffInMs >= maxMs) {
          showDialog("success", "작업최대시간에 도달했습니다.");

          // 알림을 띄운 후 타이머 종료
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            isWarned.current = false;
          }
        }
      }
    }, 1000); // 1초 간격으로 확인
  };

  const startTimer2 = () => {
    tmStart.current = new Date();

    // 기존에 돌고 있던 인터벌이 있다면 초기화
    if (intervalRef.current) clearInterval(intervalRef.current);

    // 1초마다 실시간으로 체크
    intervalRef.current = setInterval(() => {
      if (tmStart.current) {
        const diffInMs = new Date().getTime() - tmStart.current.getTime();

        setHeaderInfo((prev: any) => {
          return {
            ...prev,
            grid3: {
              ...prev.grid3,
              controls: prev.grid3.controls.map((ctrl: any) =>
                ctrl.cd_param === "timer"
                  ? {
                      ...ctrl,
                      nm_label: formatTime(diffInMs),
                    }
                  : ctrl
              ),
            },
          };
        });
      }
    }, 1000); // 1초 간격
  };

  const formatTime = (ms: number): string => {
    if (ms <= 0) return "00:00";

    const totalSeconds = Math.floor(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;

    // 한 자릿수일 때 앞에 0을 붙여줍니다. (예: 5 -> "05")
    const displayMins = String(mins).padStart(2, "0");
    const displaySecs = String(secs).padStart(2, "0");

    return `${displayMins}:${displaySecs}`;
  };

  // 26.09.10 kdy grid2에서 투입(cons)일 때만 보여야 하는 컬럼. qt_use는 계근(recal)에서도 표시한다.
  const grid2_column_cons = [
    "cd_matl",
    "nm_matl",
    "rt_matl",
    "qt_matl_adjust",
    "qt_max",
    "qt_min",
    "qt_loss",
    "qt_use",
    "no_lot",
    "cd_wh",
    "yn_input",
  ];

  const getGrid2ColumnVisible = (
    cdColumn: string,
    fgVisible: string,
    tpRecipe: string
  ) => {
    if (!grid2_column_cons.includes(cdColumn)) return fgVisible;
    if (tpRecipe === "cons") return fgVisible;
    if (tpRecipe === "recal" && cdColumn === "qt_use") return fgVisible;
    return "N";
  };

  const sortColums = (gridColumns: any[], tpRecipe: string) => {
    const sortedColumns: any[] = []; // 정렬할 컬럼
    let noSeq = 0;

    if (tpRecipe === "cons") {
      noSeq = 1;
    }

    gridColumns.forEach((row: any) => {
      noSeq += 1;
      const fgVisible = getGrid2ColumnVisible(
        row.cd_column,
        row.fg_visible,
        tpRecipe
      );
      if (tpRecipe === "cons") {
        if (row.cd_column === "st_run") {
          sortedColumns.push({
            cd_column: row.cd_column,
            fg_visible: fgVisible,
            no_seq: 1,
          });
        } else {
          sortedColumns.push({
            cd_column: row.cd_column,
            fg_visible: fgVisible,
            no_seq: noSeq,
          });
        }
      } else {
        sortedColumns.push({
          cd_column: row.cd_column,
          fg_visible: fgVisible,
          no_seq: noSeq,
        });
      }
    });

    sortedColumns.sort((a, b) => a.no_seq - b.no_seq);

    return [...sortedColumns];
  };
  //#endregion

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        // etcProps: { suppressDragLeaveHidesColumns: true }, // 26.09.07 kdy 태블릿에서 헤더 드래그로 컬럼 사라지는 것 방지
        filter: { refs: ["grid2"], columns: ["cd_group"] },
        headerInfo: headerInfo.grid1,
        events: {
          onRowDataReady: onRowDataReadyH,
          onBtnEtcClick: onBtnEtcClickH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        // etcProps: { suppressDragLeaveHidesColumns: true },
        headerInfo: headerInfo.grid2,
        events: {
          onBtnEtcClick: onBtnEtcClickL,
          onCellValueChanged: onCellValueChangedL,
          onFocusedRowChanged: onFocusedRowChangedL,
          onHeaderValueChanged: (event: any) => {
            if (event.colId === "cd_equip") {
              cdEquip.current = event.newValue;
            }
          },
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        // etcProps: { suppressDragLeaveHidesColumns: true },
        headerInfo: headerInfo.grid3,
        events: {
          onHeaderEnter: onEnter,
          onBtnEtcClick: onBtnEtcClickD,
          onCellClicked: onCellClickedD,
          onCellValueChanged: onCellValueChangedD,
          onHeaderValueChanged: (event: any) => {
            if (event.colId === "dc_ref") {
              cdBowl.current = event.newValue;
            }
          },
        },
      },
    }),
    [columns, dataSources, headerInfo]
  );

  const search = useMemo(
    () => ({
      data: formData,
      controls: formControlData,
    }),
    [formData, formControlData]
  );

  const forms = useMemo(
    () => ({
      freeform1: {
        controls: formControls.freeform1,
        data: formDataSources.freeform1,
        etcProps: {},
        events: {
          onBtnEtcClick: onBtnEtcClickFreeForm,
          onControlFocus: onControlFocusFreeForm,
        },
      },
    }),
    [formControls, formDataSources]
  );

  const dynamicProps = React.useMemo(
    () => ({
      searchRef,
      search,
      grids,
      gridRefs,
      forms,
      formRefs,
    }),
    [formData, formControlData, grids, gridRefs, forms, formRefs]
  );
  //region UI
  return <ComCreateUi dynamicProps={dynamicProps} layoutConfig={layout} />;
});

export default SN_PRD_RUN_REG_001_CHEM;
